import { useEffect, useState } from 'react';
import type { IAsset } from '@frontend/components/interfaces/asset';
import type { ICustomControlRequestReadProps } from '@frontend/components/pages/customControls/interfaces/customControlRequestReadProps';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ProcessEntity } from '@common/enums/processEntity';
import { RequestStatus } from '@common/enums/requestStatus';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getOrderedAssets } from '@common/utils/photoAssetUtils';
import { RequestReadButtons } from '@frontend/components/elements/carePathButtons/RequestReadButtons';
import { FormDivider, HeaderDivider, UserTextParagraph } from '@frontend/components/elements/common';
import { FormLabel } from '@frontend/components/elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '@frontend/components/elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '@frontend/components/elements/forms/InputContainer';
import { CustomControlCollapseBody } from '@frontend/components/pages/customControls/utils/v1/CustomControlCollapseBody';
import { MedicationsRead } from '@frontend/components/pages/customControls/utils/v1/medications/MedicationsRead';
import { ProfileImages } from '@frontend/components/pages/customControls/utils/v1/ProfileImages';
import { ServiceConsumerSupervisorRead } from '@frontend/components/pages/customControls/utils/v1/ServiceConsumerSupervisorRead';
import { TxcsMedicationsList } from '@frontend/components/pages/customControls/utils/v1/TxcsMedicationsList';
import {
    AssetThumbnailsWithHeader,
    ProcessContainer,
    ProfileImageWithName,
} from '@frontend/components/pages/episodes/common';
import { ProcessItemFooter } from '@frontend/components/pages/episodes/ProcessItemFooter';
import { ProcessItemHeader } from '@frontend/components/pages/episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataRead';
import { translateKey } from '@frontend/translate';
import type { IDermaSnelCscV1RequestAddEditCustomControlData } from './DermaSnelCscV1RequestAddEdit';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { DermaSnelCscV1FollowingSteps } from './DermaSnelCscV1FollowingSteps';
import { DermaSnelCscV1RequestSummary } from './DermaSnelCscV1RequestSummary';

export function DermaSnelCscV1RequestRead(props: ICustomControlRequestReadProps) {
    const [isFooterVisible, setIsFooterVisible] = useState(false);
    const [hasMedications, setHasMedications] = useState(false);
    const [orderedAssets, setOrderedAssets] = useState<IAsset[]>([]);
    const customControlData: IDermaSnelCscV1RequestAddEditCustomControlData = props.request.customControlData;

    if (!customControlData) {
        return null;
    }

    useEffect(() => {
        checkIsFooterVisible();
    }, [props.request, hasMedications]);

    useEffect(() => {
        setOrderedAssets(getOrderedAssets(props.request));
    }, [props.request]);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            {!props.itemState.isOpen && (
                <DermaSnelCscV1RequestSummary
                    {...props}
                    orderedAssets={orderedAssets}
                />
            )}

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {props.request.status !== RequestStatus.requested ? (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('request.sendTo')}</FormLabel>
                        <InputContainer>
                            <ProfileImageWithName
                                organization={props.request.recipientOrganization}
                                practitioner={props.request.recipientPractitioner}
                            />
                        </InputContainer>
                    </HorizontalFormGroupRead>
                ) : (
                    <ProfileImages request={props.request} />
                )}

                <ServiceConsumerSupervisorRead {...props} />

                <AssetThumbnailsWithHeader
                    assets={orderedAssets}
                    id={props.request.id}
                    withFormDivider
                />

                {isAnammeseVisible() && (
                    <>
                        <FormDivider />
                        <HeaderDivider text="Anamnese" />

                        {customControlData.moedervlek && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Moedervlek</FormLabel>
                                <InputContainer>
                                    <UserTextParagraph>{customControlData.moedervlek}</UserTextParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {isKlatchenVisible() && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Klachten</FormLabel>
                                <InputContainer>
                                    {customControlData.klachten.jeuk && <UserTextParagraph>Jeuk</UserTextParagraph>}
                                    {customControlData.klachten.pijn && <UserTextParagraph>Pijn</UserTextParagraph>}
                                    {customControlData.klachten.bloeden && (
                                        <UserTextParagraph>Bloeden</UserTextParagraph>
                                    )}
                                    {customControlData.klachten.cosmetisch && (
                                        <UserTextParagraph>Cosmetisch</UserTextParagraph>
                                    )}
                                    {customControlData.klachten.anders && (
                                        <UserTextParagraph>{customControlData.klachten.anders}</UserTextParagraph>
                                    )}
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {isVeranderingenMoedervlekVisible() && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Veranderingen moedervlek</FormLabel>
                                <InputContainer>
                                    <UserTextParagraph>{showVeranderingenMoedervlek()}</UserTextParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {customControlData.klachten_verandering_sinds && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Verandering sinds</FormLabel>
                                <InputContainer>
                                    <UserTextParagraph>
                                        {customControlData.klachten_verandering_sinds}
                                    </UserTextParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {customControlData.lichaamsdeel && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Lichaamsdeel</FormLabel>
                                <InputContainer>
                                    <UserTextParagraph>{customControlData.lichaamsdeel}</UserTextParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {customControlData.huidkanker_familie !== null && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Huidkanker in de familie</FormLabel>
                                <InputContainer>
                                    <UserTextParagraph>
                                        {customControlData.huidkanker_familie ? 'Ja' : 'Nee'}
                                    </UserTextParagraph>
                                    {customControlData.huidkanker_familie_option && (
                                        <UserTextParagraph>
                                            {' '}
                                            {customControlData.huidkanker_familie_option !== 'Anders'
                                                ? customControlData.huidkanker_familie_option
                                                : customControlData.huidkanker_familie_option_anders}
                                        </UserTextParagraph>
                                    )}
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {customControlData.huidkanker_voorgeschiedenis !== null && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Huidkanker in eigen voorgeschiedenis</FormLabel>
                                <InputContainer>
                                    <UserTextParagraph>
                                        {customControlData.huidkanker_voorgeschiedenis ? 'Ja' : 'Nee'}
                                    </UserTextParagraph>
                                    {customControlData.huidkanker_voorgeschiedenis_option && (
                                        <UserTextParagraph>
                                            {' '}
                                            {customControlData.huidkanker_voorgeschiedenis_option !== 'Anders'
                                                ? customControlData.huidkanker_voorgeschiedenis_option
                                                : customControlData.huidkanker_voorgeschiedenis_option_anders}
                                        </UserTextParagraph>
                                    )}
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}
                        <FormDivider />
                    </>
                )}
                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    onContentLoaded={async (hasContent) => setHasMedications(hasContent)}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList
                    onContentLoaded={async (hasContent) => setHasMedications(hasContent)}
                    requestId={props.request.id}
                />

                <HeaderDivider text={translateKey('request.question')} />

                {customControlData.conclusie_diagnose && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Werkdiagnose aanwezig</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.conclusie_diagnose}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {isWerkdiagnoseVisible() && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Werkdiagnose</FormLabel>
                        <InputContainer>
                            {customControlData.werkdiagnose.atypische_naevus && (
                                <UserTextParagraph>Atypische naevus</UserTextParagraph>
                            )}
                            {customControlData.werkdiagnose.naevus_naevocellularis && (
                                <UserTextParagraph>Naevus naevocellularis</UserTextParagraph>
                            )}
                            {customControlData.werkdiagnose.verdenking_melanoom && (
                                <UserTextParagraph>Verdenking melanoom</UserTextParagraph>
                            )}
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <Icd10SelectionsRead
                    diagnosis={props.request.diagnosis}
                    hideTitle
                    hideWorkingDiagnosisLabel={isWerkdiagnoseVisible()}
                    showWorkingDiagnosis
                    thesaurusTermDate={props.request.dateSent}
                />

                {isVraagVisible() && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Vraag</FormLabel>
                        <InputContainer>
                            {customControlData.vraag.verdacht_van_kwaadaardigheid && (
                                <UserTextParagraph>Verdacht van kwaadaardigheid?</UserTextParagraph>
                            )}
                            {customControlData.vraag.expectatief_beleid && (
                                <UserTextParagraph>Expectatief beleid?</UserTextParagraph>
                            )}
                            {customControlData.vraag.diagnostiek_en_behandeling && (
                                <UserTextParagraph>Insturen voor diagnostiek en behandeling?</UserTextParagraph>
                            )}
                            {customControlData.vraag.anders !== null && (
                                <UserTextParagraph>{customControlData.vraag.anders}</UserTextParagraph>
                            )}
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {customControlData.toelichting && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.toelichting}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <DermaSnelCscV1FollowingSteps />
            </CustomControlCollapseBody>

            {isFooterVisible && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideConsult')}
                    labelMore={translateKey('request.showConsult')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}

            <RequestReadButtons
                {...props}
                editRequest={handleEditRequest}
            />
        </ProcessContainer>
    );

    async function handleEditRequest() {
        // Reload process to retrieve possibly uploaded images via the app after this process was saved as draft.
        await props.reloadRequest();
        await props.editRequest();
    }

    function checkIsFooterVisible() {
        if (
            (props.request.assets && props.request.assets.length > 0) ||
            props.request.recipientOrganizationId ||
            props.request.recipientPractitionerId ||
            hasMedications ||
            isAnammeseVisible() ||
            customControlData.conclusie_diagnose ||
            isWerkdiagnoseVisible() ||
            isVraagVisible() ||
            customControlData.toelichting
        ) {
            setIsFooterVisible(true);
        }
    }

    function isAnammeseVisible() {
        return (
            customControlData.moedervlek ||
            isKlatchenVisible() ||
            isVeranderingenMoedervlekVisible() ||
            customControlData.klachten_verandering_sinds ||
            customControlData.lichaamsdeel ||
            customControlData.huidkanker_familie !== null ||
            customControlData.huidkanker_voorgeschiedenis !== null
        );
    }

    function isKlatchenVisible() {
        return (
            customControlData.klachten.jeuk ||
            customControlData.klachten.pijn ||
            customControlData.klachten.bloeden ||
            customControlData.klachten.cosmetisch ||
            customControlData.klachten.anders !== null
        );
    }

    function isVeranderingenMoedervlekVisible() {
        return (
            customControlData.veranderingen_moedervlek.grootte ||
            customControlData.veranderingen_moedervlek.vorm ||
            customControlData.veranderingen_moedervlek.kleur
        );
    }

    function isWerkdiagnoseVisible() {
        return (
            customControlData.werkdiagnose.atypische_naevus ||
            customControlData.werkdiagnose.naevus_naevocellularis ||
            customControlData.werkdiagnose.verdenking_melanoom
        );
    }

    function isVraagVisible() {
        return (
            customControlData.vraag.verdacht_van_kwaadaardigheid ||
            customControlData.vraag.expectatief_beleid ||
            customControlData.vraag.diagnostiek_en_behandeling ||
            customControlData.vraag.anders !== null
        );
    }

    function showVeranderingenMoedervlek() {
        const labels = [];

        if (customControlData.veranderingen_moedervlek.grootte) {
            labels.push('Grootte');
        }

        if (customControlData.veranderingen_moedervlek.vorm) {
            labels.push('Vorm');
        }
        if (customControlData.veranderingen_moedervlek.kleur) {
            labels.push('Kleur(en)');
        }

        return labels.join(', ');
    }
}
